import { useState, useEffect, useCallback, useMemo } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { Plus, X, ChevronDown, ChevronUp, Crown, Check, AlertCircle } from 'lucide-react'
import VendorBudgetSuggestions from '../../../components/aisle/VendorBudgetSuggestions'
import { useFeatureFlags } from '../../../lib/featureFlags'
import PageTip from '../../../components/PageTip'

// ── Config ────────────────────────────────────────────────
const CURRENCIES = [
  { code:'NGN', symbol:'₦' }, { code:'USD', symbol:'$' },
  { code:'GBP', symbol:'£' }, { code:'EUR', symbol:'€' },
  { code:'GHS', symbol:'₵' }, { code:'KES', symbol:'KSh' },
  { code:'ZAR', symbol:'R'  },
]

// Free plan total-budget ceiling — ₦500,000 and rough fixed equivalents at
// today's rates (kept perpetually; it's a deliberately soft "demo" cap).
const FREE_BUDGET_CAP = { NGN:500000, USD:300, GBP:250, EUR:300, GHS:5000, KES:40000, ZAR:6000 }

const PRESET_CATEGORIES = [
  { name:'Venue & Hall',           icon:'🏛️' },
  { name:'Catering & Food',        icon:'🍽️' },
  { name:'Photography & Video',    icon:'📸' },
  { name:'Music & Entertainment',  icon:'🎵' },
  { name:'Décor & Flowers',        icon:'💐' },
  { name:"Bride's Attire",         icon:'👗' },
  { name:"Groom's Attire",         icon:'🤵' },
  { name:'Invitations & Printing', icon:'✉️' },
  { name:'Transportation',         icon:'🚗' },
  { name:'Honeymoon',              icon:'✈️' },
  { name:'Miscellaneous',          icon:'📦' },
]

// 4 bright distinct colours for summary bars
const STAT_COLORS = {
  allocated: { main:'#7A917A', bg:'rgba(122,145,122,0.12)', label:'Total Allocated'  },
  committed: { main:'#C4826A', bg:'rgba(196,130,106,0.12)', label:'Total Committed'  },
  paid:      { main:'#9BAF9B', bg:'rgba(155,175,155,0.15)', label:'Paid So Far'      },
  owed:      { main:'#A86650', bg:'rgba(168,102,80,0.1)',   label:'Still Owed'       },
}

const PIE_COLORS = ['#9BAF9B','#9BAF9B','#C4826A','#9BAF9B','#C4826A','#C4826A','#06B6D4','#EC4899','#84CC16','#6366F1','#14B8A6']

// ── Health helpers ─────────────────────────────────────────
function spentPct(committed, allocated) {
  return allocated > 0 ? (committed / allocated) * 100 : 0
}
function badgeStyle(pct) {
  if (pct >= 100) return { bg:'rgba(220,38,38,0.1)',   color:'#B91C1C', border:'1px solid rgba(220,38,38,0.3)' }
  if (pct >= 90)  return { bg:'rgba(179,107,85,0.12)', color:'#9A4A28', border:'1px solid rgba(179,107,85,0.3)' }
  if (pct >= 70)  return { bg:'rgba(217,119,6,0.1)',   color:'#B45309', border:'1px solid rgba(217,119,6,0.3)' }
  return           { bg:'rgba(82,163,105,0.12)', color:'#3D8B5E', border:'1px solid rgba(82,163,105,0.3)' }
}
function barColor(pct) {
  if (pct >= 100) return '#DC2626'
  if (pct >= 90)  return '#B45309'
  if (pct >= 70)  return '#D97706'
  return '#B36B55'
}
function healthPill(pct) {
  if (pct >= 100) return { label:'Over Budget', color:'#B91C1C', bg:'rgba(220,38,38,0.1)' }
  if (pct >= 90)  return { label:'Tight',       color:'#9A4A28', bg:'rgba(179,107,85,0.12)' }
  if (pct >= 70)  return { label:'Monitor',     color:'#B45309', bg:'rgba(217,119,6,0.1)' }
  return           { label:'On Track',   color:'#3D8B5E', bg:'rgba(82,163,105,0.12)' }
}

// ── SVG Category Icons ─────────────────────────────────────
const CATEGORY_ICONS = {
  'Venue & Hall':          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M6 21V7l6-4 6 4v14M9 21v-6h6v6"/></svg>,
  'Catering & Food':       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>,
  'Photography & Video':   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  'Music & Entertainment': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  'Décor & Flowers':       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V12M12 12C12 12 8 9 8 6a4 4 0 018 0c0 3-4 6-4 6z"/><path d="M12 12C12 12 16 9 16 6"/><path d="M12 12C8 12 5 15 5 15"/><path d="M12 12c4 0 7 3 7 3"/></svg>,
  "Bride's Attire":        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.38 3.46L16 2l-1.5 3.5-3-3.5-3 3.5L7 2 2.62 3.46a1 1 0 00-.64 1.28L6 20h12l4.02-15.26a1 1 0 00-.64-1.28z"/></svg>,
  "Groom's Attire":        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.38 3.46L16 2l-1.5 3.5-3-3.5-3 3.5L7 2 2.62 3.46a1 1 0 00-.64 1.28L6 20h12l4.02-15.26a1 1 0 00-.64-1.28z"/></svg>,
  'Invitations & Printing':<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  'Transportation':        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  'Honeymoon':             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19 4c-1 0-3 1-3.5 2.5L11 10 2.8 8.2c-.5-.1-1 .4-.8.9L6 22l4-4 4 4 3.8-3.8z"/></svg>,
  'Miscellaneous':         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  'Contingency / Buffer':  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
}
const DEFAULT_ICON = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C7B70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>

function CategorySvgIcon({ name }) {
  const icon = CATEGORY_ICONS[name] || DEFAULT_ICON
  return (
    <div style={{ width:36, height:36, borderRadius:8, background:'rgba(140,123,112,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {icon}
    </div>
  )
}

// ── Pie Chart ─────────────────────────────────────────────
function PieChart({ categories }) {
  const total = categories.reduce((s,c)=>s+Number(c.allocated||0),0)
  if (!total) return (
    <div style={{ width:150,height:150,borderRadius:'50%',background:'white',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto' }}>
      <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.7rem',color:'#BCBCBC',textAlign:'center' }}>No allocations</p>
    </div>
  )
  let cum = -90
  const R=65, cx=75, cy=75
  const slices = categories.filter(c=>Number(c.allocated)>0).map((c,i)=>{
    const pct=Number(c.allocated)/total, angle=pct*360, start=cum
    cum+=angle
    return { ...c, pct, angle, start, color:PIE_COLORS[i%PIE_COLORS.length] }
  })
  const toXY=(a,r)=>({ x:cx+r*Math.cos(a*Math.PI/180), y:cy+r*Math.sin(a*Math.PI/180) })
  const arc=(s,e,r)=>{
    const sv=toXY(s,r),ev=toXY(e,r),lg=e-s>180?1:0
    return `M${cx} ${cy} L${sv.x} ${sv.y} A${r} ${r} 0 ${lg} 1 ${ev.x} ${ev.y}Z`
  }
  return (
    <svg width={150} height={150} viewBox="0 0 150 150">
      {slices.map((s,i)=><path key={i} d={arc(s.start,s.start+s.angle,R)} fill={s.color} opacity={0.9}/>)}
      <circle cx={cx} cy={cy} r={38} fill="white"/>
      <text x={cx} y={cy-4} textAnchor="middle" fontFamily="Gibs,serif" fontSize="11" fill="#1A1A1A">{slices.length}</text>
      <text x={cx} y={cy+10} textAnchor="middle" fontFamily="Nunito Sans,sans-serif" fontSize="8" fill="#6B6B6B">categories</text>
    </svg>
  )
}

// ── Category Card ─────────────────────────────────────────
function CategoryCard({ cat, allItems, totalAllocated, onRefresh, currency, onItemChange, capLimit = Infinity, committedTotal = 0, onUpgrade }) {
  const [expanded, setExpanded]   = useState(false)
  const [editAlloc, setEditAlloc] = useState(false)
  const [allocVal, setAllocVal]   = useState(cat.allocated||0)
  const [deleting, setDeleting]   = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  const [editDesc, setEditDesc]   = useState('')
  const [editAmt, setEditAmt]     = useState('')
  const [newDesc, setNewDesc]     = useState('')
  const [newAmt, setNewAmt]       = useState('')
  const [newPaid, setNewPaid]     = useState(false)
  const [addingInline, setAddingInline] = useState(false)
  const [menuOpen, setMenuOpen]         = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [revertItem, setRevertItem]     = useState(null) // item id showing revert prompt
  const [capErr, setCapErr]             = useState(false) // adding this item would pass the free cap

  const myItems = allItems.filter(i=>i.category_id===cat.id)
  const fmt = n => currency.symbol+Number(n||0).toLocaleString()
  const committed = myItems.reduce((s,i)=>s+Number(i.amount),0)
  const paid      = myItems.filter(i=>i.paid).reduce((s,i)=>s+Number(i.amount),0)
  const allocated = Number(cat.allocated||0)
  const pct       = allocated>0?Math.min((committed/allocated)*100,100):0
  const over      = committed>allocated&&allocated>0
  const budgetPct = totalAllocated>0?((allocated/totalAllocated)*100).toFixed(1):0
  const sliceColor = PIE_COLORS[PRESET_CATEGORIES.findIndex(p=>p.name===cat.name)%PIE_COLORS.length]||'#9BAF9B'

  async function saveAlloc() {
    const v = parseFloat(allocVal)||0
    await supabase.from('budget_categories').update({ allocated:v }).eq('id',cat.id)
    setEditAlloc(false); onRefresh()
  }

  async function addItemInline() {
    if (!newDesc.trim()||!newAmt) return
    // Free plan: hard-block once tracked spend would pass the cap.
    if (committedTotal + (parseFloat(newAmt)||0) > capLimit) { setCapErr(true); return }
    setCapErr(false)
    setAddingInline(true)
    const { data } = await supabase.from('budget_items')
      .insert({ category_id:cat.id,description:newDesc.trim(),amount:parseFloat(newAmt),paid:newPaid })
      .select().single()
    if (data) onItemChange('add', data)
    setNewDesc(''); setNewAmt(''); setNewPaid(false)
    setAddingInline(false)
  }

  async function togglePaid(item) {
    const { data } = await supabase.from('budget_items').update({ paid:!item.paid }).eq('id',item.id).select().single()
    if (data) onItemChange('update', data)
  }

  async function deleteItem(id) {
    setDeleting(id)
    await supabase.from('budget_items').delete().eq('id',id)
    onItemChange('delete', { id, category_id:cat.id, amount: myItems.find(i=>i.id===id)?.amount||0 })
    setDeleting(null)
  }

  async function startEdit(item) { setEditingItem(item.id); setEditDesc(item.description); setEditAmt(String(item.amount||'')) }
  async function saveEdit(item) {
    await supabase.from('budget_items').update({ description:editDesc.trim(), amount:Number(editAmt)||0 }).eq('id',item.id)
    setEditingItem(null); onItemChange()
  }
  async function duplicateItem(item) {
    const { data } = await supabase.from('budget_items')
      .insert({ category_id:item.category_id,description:`${item.description} (copy)`,amount:item.amount,paid:false })
      .select().single()
    if (data) onItemChange('add', data)
  }

  async function deleteCategory() {
    // Delete all items first
    await supabase.from('budget_items').delete().eq('category_id',cat.id)
    await supabase.from('budget_categories').delete().eq('id',cat.id)
    onRefresh()
  }

  return (
    <div style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'1rem',borderLeft:`4px solid ${sliceColor}`,position:'relative' }}>
      <div onClick={()=>setExpanded(!expanded)} style={{ padding:'1rem 1.25rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.875rem',userSelect:'none' }}>
        <CategorySvgIcon name={cat.name}/>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.35rem',gap:'0.5rem' }}>
            <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:0 }}>{cat.name}</p>
            <div style={{ display:'flex',alignItems:'center',gap:'0.5rem',flexShrink:0 }}>
              {over&&<AlertCircle size={13} color="#EF4444"/>}
              <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.72rem',fontWeight:600,color:over?'#C4826A':'#6B6B6B' }}>{fmt(committed)} / {fmt(allocated)}</span>
              {(()=>{ const sp=spentPct(committed,allocated); const bs=badgeStyle(sp); return (
                <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.65rem',fontWeight:700,borderRadius:100,padding:'2px 8px',...bs }}>{sp>=100?'⚠ ':''}{Math.round(sp)}%</span>
              )})()}
            </div>
          </div>
          <div style={{ height:6,background:'#F0EBE3',borderRadius:100,overflow:'hidden' }}>
            <div style={{ height:'100%',width:`${Math.min(pct,100)}%`,background:barColor(spentPct(committed,allocated)),borderRadius:100,transition:'width 0.3s ease' }}/>
          </div>
          {over && <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.62rem',color:'#DC2626',margin:'0.1rem 0 0',fontWeight:600 }}>{currency.symbol}{(committed-allocated).toLocaleString()} over budget</p>}
          <div style={{ display:'flex',justifyContent:'space-between',marginTop:'0.2rem' }}>
            <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.62rem',color:'#9BAF9B',fontWeight:700 }}>Paid: {fmt(paid)}</span>
            <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.62rem',color:'#C4826A' }}>Committed: {fmt(committed)}</span>
          </div>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:'0.25rem',flexShrink:0 }}>
          <div style={{ position:'relative', zIndex: menuOpen ? 100 : 'auto' }}>
            <button onClick={e=>{e.stopPropagation();setMenuOpen(m=>!m)}} style={{ width:32,height:32,background:'none',border:'none',cursor:'pointer',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',color:'#9B9B9B' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
            </button>
            {menuOpen && (
              <div onClick={e=>e.stopPropagation()} style={{ position:'absolute',right:0,top:'100%',background:'white',border:'1.5px solid #E5E0D8',borderRadius:'0.875rem',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',zIndex:200,minWidth:180,padding:'0.375rem',marginTop:4 }}>
                <button onClick={()=>{ setMenuOpen(false); const n=window.prompt('Rename category:',cat.name); if(n&&n.trim()&&n.trim()!==cat.name) supabase.from('budget_categories').update({name:n.trim()}).eq('id',cat.id).then(onRefresh) }} style={{ display:'block',width:'100%',textAlign:'left',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#1A1A1A',background:'none',border:'none',padding:'0.5rem 0.75rem',borderRadius:'0.5rem',cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#F8F4F0'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  Rename Category
                </button>
                <button onClick={()=>{ setMenuOpen(false); setConfirmDelete(true) }} style={{ display:'block',width:'100%',textAlign:'left',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#DC2626',background:'none',border:'none',padding:'0.5rem 0.75rem',borderRadius:'0.5rem',cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#FEF2F2'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  Remove Category
                </button>
              </div>
            )}
          </div>
          {expanded?<ChevronUp size={15} color="#BCBCBC"/>:<ChevronDown size={15} color="#BCBCBC"/>}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div onClick={e=>e.stopPropagation()} style={{ borderTop:'1px solid #FEE2E2',background:'#FFF5F5',padding:'1rem 1.25rem' }}>
          <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.85rem',color:'#DC2626',margin:'0 0 0.25rem' }}>Remove {cat.name}?</p>
          <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:'0 0 0.875rem',lineHeight:1.6 }}>This will delete all expenses in this category.</p>
          <div style={{ display:'flex',gap:'0.5rem' }}>
            <button onClick={()=>setConfirmDelete(false)} style={{ flex:1,fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#6B6B6B',background:'#F3F0EA',border:'none',borderRadius:100,padding:'0.55rem',cursor:'pointer' }}>Cancel</button>
            <button onClick={()=>{ setConfirmDelete(false); deleteCategory() }} style={{ flex:1,fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:700,color:'white',background:'#DC2626',border:'none',borderRadius:100,padding:'0.55rem',cursor:'pointer' }}>Yes, Remove</button>
          </div>
        </div>
      )}

      {expanded&&(
        <div style={{ borderTop:'1px solid #F3F0EA',padding:'1rem 1.25rem' }}>
          {/* Step 1: Allocation */}
          <div style={{ background:'rgba(155,175,155,0.06)',border:'1px solid rgba(155,175,155,0.15)',borderRadius:'0.75rem',padding:'0.75rem 1rem',marginBottom:'0.875rem' }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem' }}>
              <div>
                <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',color:'#1A1A1A',fontWeight:700 }}>Budget Allocation</span>
                <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.65rem',color:'#9BAF9B',margin:'0.1rem 0 0' }}>Money set aside for this category</p>
              </div>
              {editAlloc?(
                <div style={{ display:'flex',gap:'0.35rem',alignItems:'center' }}>
                  <input value={allocVal} onChange={e=>setAllocVal(e.target.value)} type="number" autoFocus onKeyDown={e=>e.key==='Enter'&&saveAlloc()}
                    style={{ width:110,fontFamily:'Nunito Sans,sans-serif',fontSize:'0.8rem',padding:'0.3rem 0.5rem',border:'1.5px solid #9BAF9B',borderRadius:'0.5rem',outline:'none' }}/>
                  <button onClick={saveAlloc} style={{ background:'#9BAF9B',border:'none',borderRadius:'0.5rem',padding:'0.3rem 0.6rem',cursor:'pointer',color:'white' }}><Check size={13}/></button>
                  <button onClick={()=>setEditAlloc(false)} style={{ background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={13}/></button>
                </div>
              ):(
                <button onClick={e=>{ e.stopPropagation(); setEditAlloc(true) }} style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:100,padding:'0.3rem 0.75rem',cursor:'pointer',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.82rem',color:'#1A1A1A',fontWeight:700 }}>
                  {allocated>0?`${fmt(allocated)} ✎`:'Set amount ✎'}
                </button>
              )}
            </div>
          </div>

          {/* Step 2: Expenses — only show when allocation is set */}
          {allocated>0&&<>
          {/* Items */}
          {myItems.length>0&&(
            <div style={{ display:'flex',flexDirection:'column',gap:'0.35rem',marginBottom:'0.75rem' }}>
              {myItems.map((item,idx)=>(
                <div key={item.id} style={{ borderRadius:'0.625rem',border:'1px solid #F3F0EA',background:'#FFFFFF',overflow:'hidden' }}>
                  {editingItem===item.id ? (
                    <div style={{ display:'flex',gap:'0.4rem',alignItems:'center',padding:'0.5rem 0.75rem',flexWrap:'wrap' }}>
                      <input value={editDesc} onChange={e=>setEditDesc(e.target.value)} autoFocus placeholder="Description"
                        style={{ flex:'1 1 120px',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.82rem',padding:'0.35rem 0.6rem',border:'1.5px solid #9BAF9B',borderRadius:'0.5rem',outline:'none' }}/>
                      <input value={editAmt} onChange={e=>setEditAmt(e.target.value)} type="number" placeholder="Amount"
                        style={{ width:90,fontFamily:'Nunito Sans,sans-serif',fontSize:'0.82rem',padding:'0.35rem 0.6rem',border:'1.5px solid #9BAF9B',borderRadius:'0.5rem',outline:'none' }}/>
                      <button onClick={()=>saveEdit(item)} style={{ background:'#9BAF9B',border:'none',borderRadius:'0.5rem',padding:'0.35rem 0.75rem',cursor:'pointer',color:'white',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:700 }}><Check size={13}/></button>
                      <button onClick={()=>setEditingItem(null)} style={{ background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={13}/></button>
                    </div>
                  ) : (
                    <div style={{ display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.5rem 0.75rem' }}>
                      <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#BCBCBC',width:18,textAlign:'center',flexShrink:0 }}>{idx+1}.</span>
                      <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.8rem',color:item.paid?'#9BAF9B':'#1A1A1A',flex:1,textDecoration:item.paid?'line-through':'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{item.description}</span>
                      <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#1A1A1A',flexShrink:0 }}>{fmt(item.amount)}</span>
                      {/* Mark Paid — right side */}
                      {revertItem===item.id ? (
                        <div style={{ display:'flex',gap:'0.25rem',flexShrink:0 }}>
                          <button onClick={()=>{ togglePaid(item); setRevertItem(null) }} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.6rem',fontWeight:700,padding:'3px 8px',borderRadius:100,border:'none',background:'#F3F0EA',color:'#6B6B6B',cursor:'pointer',whiteSpace:'nowrap' }}>Mark as Unpaid</button>
                          <button onClick={()=>setRevertItem(null)} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.6rem',fontWeight:700,padding:'3px 8px',borderRadius:100,border:'none',background:'none',color:'#BCBCBC',cursor:'pointer' }}>✕</button>
                        </div>
                      ) : (
                        <button onClick={()=>item.paid ? setRevertItem(item.id) : togglePaid(item)} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.6rem',fontWeight:700,padding:'3px 9px',borderRadius:100,border:'none',background:item.paid?'#9BAF9B':'rgba(196,130,106,0.15)',color:item.paid?'white':'#C4826A',cursor:'pointer',flexShrink:0,whiteSpace:'nowrap' }}>
                          {item.paid?'Paid ✓':'Mark Paid'}
                        </button>
                      )}
                      {/* 3-dot action menu — uses position:fixed to escape overflow:hidden parents */}
                      <div style={{ flexShrink:0 }}>
                        <button
                          onClick={e=>{
                            e.stopPropagation()
                            const isOpen = editingItem===`menu_${item.id}`
                            if (isOpen) { setEditingItem(null); setMenuAnchor(null); return }
                            const rect = e.currentTarget.getBoundingClientRect()
                            setMenuAnchor({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
                            setEditingItem(`menu_${item.id}`)
                          }}
                          style={{ background:'none',border:'none',cursor:'pointer',minWidth:44,minHeight:44,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:6,color:'#BCBCBC' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                        </button>
                        {editingItem===`menu_${item.id}` && menuAnchor && (
                          <>
                            {/* Invisible backdrop to close menu on outside click */}
                            <div onClick={()=>{ setEditingItem(null); setMenuAnchor(null) }} style={{ position:'fixed',inset:0,zIndex:299 }}/>
                            <div style={{ position:'fixed',top:menuAnchor.top,right:menuAnchor.right,background:'white',border:'1.5px solid #E5E0D8',borderRadius:'0.75rem',boxShadow:'0 8px 20px rgba(0,0,0,0.18)',zIndex:300,minWidth:140,padding:'0.25rem' }}>
                              <button onClick={()=>{ setEditingItem(null); setMenuAnchor(null); startEdit(item) }} style={{ display:'block',width:'100%',textAlign:'left',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#1A1A1A',background:'none',border:'none',padding:'0.5rem 0.75rem',borderRadius:'0.5rem',cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#F8F4F0'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                                Edit
                              </button>
                              <button onClick={()=>{ setEditingItem(null); setMenuAnchor(null); deleteItem(item.id) }} style={{ display:'block',width:'100%',textAlign:'left',fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#DC2626',background:'none',border:'none',padding:'0.5rem 0.75rem',borderRadius:'0.5rem',cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='#FEF2F2'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                                {deleting===item.id?'Removing…':'Remove'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Inline add */}
          <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#8C7B70',letterSpacing:'0.12em',textTransform:'uppercase',margin:'0 0 0.4rem' }}>Add Expense</p>
          {committedTotal >= capLimit ? (
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.75rem',flexWrap:'wrap',background:'rgba(184,134,11,0.06)',border:'1px solid rgba(184,134,11,0.25)',borderRadius:'0.625rem',padding:'0.625rem 0.875rem',marginBottom:'0.75rem' }}>
              <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.74rem',color:'#6B5A2A',margin:0,lineHeight:1.5 }}>You've reached the free tracking limit of <strong>{currency.symbol}{Number(capLimit).toLocaleString()}</strong>. Upgrade to keep adding expenses.</p>
              <button onClick={onUpgrade} style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.72rem',color:'white',background:'linear-gradient(135deg,#B8860B,#D4AF37)',border:'none',borderRadius:100,padding:'0.4rem 0.9rem',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0 }}>Upgrade</button>
            </div>
          ) : (
            <>
              <div style={{ display:'flex',gap:'0.4rem',marginBottom:capErr?'0.4rem':'0.75rem',flexWrap:'wrap' }}>
                <input className="input-field" placeholder="Description" value={newDesc} onChange={e=>setNewDesc(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&addItemInline()} style={{ flex:'1 1 130px' }}/>
                <input className="input-field" type="number" placeholder="Amount" value={newAmt} onChange={e=>{setNewAmt(e.target.value);setCapErr(false)}}
                  onKeyDown={e=>e.key==='Enter'&&addItemInline()} style={{ width:90 }}/>
                <button className="btn-primary" onClick={addItemInline} disabled={addingInline||!newDesc.trim()||!newAmt}
                  style={{ padding:'0.625rem 0.875rem',display:'flex',alignItems:'center',gap:'0.3rem',flexShrink:0,fontSize:'0.78rem' }}>
                  <Plus size={13}/>{addingInline?'…':'Add'}
                </button>
              </div>
              {capErr && (
                <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.72rem',color:'#92400E',margin:'0 0 0.75rem',lineHeight:1.5 }}>
                  That would pass your free limit of {currency.symbol}{Number(capLimit).toLocaleString()} ({currency.symbol}{Number(Math.max(0,capLimit-committedTotal)).toLocaleString()} left). <button onClick={onUpgrade} style={{ background:'none',border:'none',padding:0,color:'#B8860B',fontWeight:700,cursor:'pointer',fontFamily:'inherit',fontSize:'inherit',textDecoration:'underline' }}>Upgrade</button> to add more.
                </p>
              )}
            </>
          )}

          </>}
          {allocated===0&&(
            <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.75rem',color:'#BCBCBC',textAlign:'center',padding:'0.5rem',margin:0 }}>Set your allocation above to start adding expenses.</p>
          )}


        </div>
      )}
    </div>
  )
}

// ── Premium Gate ──────────────────────────────────────────
function PremiumGate({ navigate, currency }) {
  return (
    <div style={{ position:'relative' }}>
      <div style={{ filter:'blur(3px)',pointerEvents:'none',opacity:0.4 }}>
        {[{name:'Venue & Hall',icon:'🏛️',allocated:500000,committed:320000},
          {name:'Catering & Food',icon:'🍽️',allocated:300000,committed:180000},
          {name:'Photography',icon:'📸',allocated:150000,committed:150000}].map((c,i)=>(
          <div key={i} style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'1rem',padding:'1rem 1.25rem',marginBottom:'0.625rem',borderLeft:`4px solid ${PIE_COLORS[i]}` }}>
            <div style={{ display:'flex',alignItems:'center',gap:'0.75rem' }}>
              <span style={{ fontSize:'1.3rem' }}>{c.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:'0.3rem' }}>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.875rem' }}>{c.name}</span>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.72rem',color:'#6B6B6B' }}>{currency.symbol}{c.committed.toLocaleString()} / {currency.symbol}{c.allocated.toLocaleString()}</span>
                </div>
                <div style={{ height:6,background:'white',borderRadius:100 }}>
                  <div style={{ height:'100%',width:`${(c.committed/c.allocated)*100}%`,background:PIE_COLORS[i],borderRadius:100 }}/>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center' }}>
        <div style={{ background:'white',border:'1.5px solid #E5E0D8',borderRadius:'1.25rem',padding:'2rem',textAlign:'center',maxWidth:340,boxShadow:'0 8px 32px rgba(0,0,0,0.1)' }}>
          <div style={{ width:52,height:52,background:'linear-gradient(135deg,rgba(184,134,11,0.12),rgba(212,175,55,0.15))',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 1rem' }}>
            <Crown size={22} color="#C9A84C"/>
          </div>
          <h3 style={{ fontFamily:'Gibs,serif',fontWeight:400,fontSize:'1.15rem',color:'#1A1A1A',margin:'0 0 0.5rem' }}>Budget Tracker is Premium</h3>
          <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.82rem',color:'#6B6B6B',lineHeight:1.65,margin:'0 0 1.25rem' }}>Track every expense, see budget health at a glance, manage categories, and export — all with Premium.</p>
          <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ width:'100%',justifyContent:'center',display:'flex',alignItems:'center',gap:'0.5rem',background:'linear-gradient(135deg,#B8860B,#D4AF37)' }}>
            <Crown size={14}/> Upgrade to Unlock
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function BudgetPage() {
  const { wedding, isPremium } = useWedding()
  const { flags }      = useFeatureFlags()
  const navigate       = useNavigate()

  const [categories, setCategories] = useState([])
  const [allItems, setAllItems]     = useState([])   // flat list — all budget items
  const [totalBudget, setTotalBudget] = useState(0)
  const [editTotal, setEditTotal]   = useState(false)
  const [totalInput, setTotalInput] = useState('')   // separate input state
  const [currency, setCurrency]     = useState(CURRENCIES[0])
  const [showPreset, setShowPreset] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customIcon, setCustomIcon] = useState('📦')
  const [loading, setLoading]       = useState(true)

  const fmt = useCallback(n=>currency.symbol+Number(n||0).toLocaleString(),[currency])
  const freeCap = FREE_BUDGET_CAP[currency.code] ?? FREE_BUDGET_CAP.NGN   // free total-budget ceiling

  // ── Fetch ──
  const fetchAll = useCallback(async () => {
    if (!wedding?.id) return
    setLoading(true)
    const { data: cats } = await supabase.from('budget_categories')
      .select('*').eq('wedding_id',wedding.id)
      .order('created_at',{ ascending:false })         // ← most recent first

    const catList = cats||[]
    setCategories(catList)

    if (catList.length) {
      const { data:itms } = await supabase.from('budget_items')
        .select('*').in('category_id',catList.map(c=>c.id))
      setAllItems(itms||[])
    } else { setAllItems([]) }

    // Load wedding meta — only set if not currently editing
    const { data:w } = await supabase.from('weddings').select('total_budget,currency').eq('id',wedding.id).single()
    if (w) {
      setTotalBudget(Number(w.total_budget)||0)
      setTotalInput(String(w.total_budget||''))
      if (w.currency) setCurrency(CURRENCIES.find(c=>c.code===w.currency)||CURRENCIES[0])
    }
    setLoading(false)
  },[wedding?.id])

  useEffect(()=>{ fetchAll() },[fetchAll])

  // ── Real-time item change handler ──
  function handleItemChange(type, item) {
    setAllItems(prev=>{
      if (type==='add')    return [...prev, item]
      if (type==='update') return prev.map(i=>i.id===item.id?item:i)
      if (type==='delete') return prev.filter(i=>i.id!==item.id)
      return prev
    })
  }

  // ── Live summary — computed from state, always fresh ──
  const summary = useMemo(()=>({
    allocated: categories.reduce((s,c)=>s+Number(c.allocated||0),0),
    committed: allItems.reduce((s,i)=>s+Number(i.amount),0),
    paid:      allItems.filter(i=>i.paid).reduce((s,i)=>s+Number(i.amount),0),
    owed:      allItems.filter(i=>!i.paid).reduce((s,i)=>s+Number(i.amount),0),
  }),[categories,allItems])

  // ── Save total budget ──
  async function saveTotal() {
    let val = parseFloat(totalInput)||0
    // Free plan: clamp the total budget to the cap.
    if (!isPremium && val > freeCap) { val = freeCap; setTotalInput(String(freeCap)) }
    await supabase.from('weddings').update({ total_budget:val, currency:currency.code }).eq('id',wedding.id)
    setTotalBudget(val)
    setEditTotal(false)

    // Auto-correct Miscellaneous to 10% of new total
    const misc = categories.find(c=>c.name==='Miscellaneous')
    if (misc && val>0) {
      const tenPct = Math.round(val*0.1)
      await supabase.from('budget_categories').update({ allocated:tenPct }).eq('id',misc.id)
      setCategories(prev=>prev.map(c=>c.id===misc.id?{...c,allocated:tenPct}:c))
    }
  }

  // ── Add category ──
  async function addPreset(preset) {
    await supabase.from('budget_categories').insert({
      wedding_id:wedding.id, name:preset.name, icon:preset.icon, allocated:0
    })
    await fetchAll(); setShowPreset(false)
  }

  async function addCustom() {
    if (!customName.trim()) return
    await supabase.from('budget_categories').insert({
      wedding_id:wedding.id, name:customName.trim(), icon:customIcon||'📦', allocated:0
    })
    setCustomName(''); setCustomIcon('📦')
    await fetchAll(); setShowPreset(false)
  }

  async function addContingency() {
    const suggested = Math.round(totalBudget*0.1)||0
    await supabase.from('budget_categories').insert({
      wedding_id:wedding.id, name:'Contingency / Buffer', icon:'⚡', allocated:suggested, is_contingency:true
    })
    await fetchAll()
  }

  // ── Export CSV ──
  function exportCSV() {
    const rows = [['Category','Description','Amount','Paid']]
    categories.forEach(cat=>{
      const catItems = allItems.filter(i=>i.category_id===cat.id)
      catItems.forEach(item=>{
        rows.push([cat.name, item.description, item.amount, item.paid?'Yes':'No'])
      })
      if (!catItems.length) rows.push([cat.name,'(no items)',cat.allocated,''])
    })
    const csv = rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')
    const blob = new Blob([csv],{ type:'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href=url; a.download=`wedding-budget-${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const remaining  = totalBudget - summary.committed
  const overBudget = summary.committed > totalBudget && totalBudget > 0
  const healthPct  = totalBudget>0 ? Math.min((summary.committed/totalBudget)*100,100) : 0
  const hasContingency = categories.some(c=>c.is_contingency)

  if (!wedding) return null

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:'1.5rem' }}>
      <style>{`
        @media (max-width:560px) {
          .bt-header { flex-direction:column; align-items:stretch !important; gap:0.875rem !important; }
          .bt-header-actions { width:100%; }
          .bt-header-actions > button { flex:1; justify-content:center; }
          .bt-total-row { flex-direction:column; align-items:flex-start !important; gap:0.5rem !important; }
          .bt-total-btn { padding-left:0 !important; }
          .bt-health-row { flex-direction:column !important; align-items:flex-start !important; gap:0.35rem !important; }
        }
      `}</style>

      {/* Header */}
      <div className="bt-header" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.75rem' }}>
        <div>
          <h1 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.6rem',color:'#1A1A1A',margin:'0 0 0.15rem',whiteSpace:'nowrap',letterSpacing:'-0.03em' }}>Budget Tracker</h1>
          <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.8rem',color:'#9B9B9B',margin:0 }}>Every naira tracked. Every category live.</p>
        </div>
        <div className="bt-header-actions" style={{ display:'flex',gap:'0.5rem',alignItems:'center',flexShrink:0 }}>
          {isPremium && (
            <button onClick={exportCSV} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.75rem',fontWeight:600,color:'#6B6B6B',background:'white',border:'1.5px solid #E5E0D8',borderRadius:100,padding:'0.45rem 0.875rem',cursor:'pointer',whiteSpace:'nowrap' }}>
              ↓ CSV
            </button>
          )}
          <button className="btn-primary" onClick={()=>setShowPreset(true)} style={{ display:'flex',alignItems:'center',gap:'0.35rem',fontSize:'0.8rem',padding:'0.45rem 0.875rem',whiteSpace:'nowrap' }}>
            <Plus size={14}/> Add Category
          </button>
        </div>
      </div>

      <PageTip id="budget" title="Track Every Money"
        points={[
          'Set your total budget at the top.',
          'Add categories, then log what you spend in each.',
          'The health bar keeps you on track at a glance.',
        ]}/>

      {/* Total Budget Input */}
      <div className="card" style={{ padding:'1.25rem' }}>
        <div className="bt-total-row" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'1rem' }}>
          <div>
            <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#1A1A1A',margin:'0 0 0.2rem' }}>Total Wedding Budget</p>
            <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.73rem',color:'#6B6B6B',margin:0 }}>Sets the baseline for all health calculations</p>
          </div>
          {editTotal ? (
            <div style={{ display:'flex',gap:'0.5rem',alignItems:'center' }}>
              <span style={{ fontFamily:'Gibs,serif',fontSize:'1.1rem',color:'#6B6B6B' }}>{currency.symbol}</span>
              <input value={totalInput} onChange={e=>setTotalInput(e.target.value)} type="number" autoFocus
                onKeyDown={e=>e.key==='Enter'&&saveTotal()}
                style={{ width:160,fontFamily:'Nunito Sans,sans-serif',fontSize:'1rem',padding:'0.5rem 0.75rem',border:'2px solid #3B82F6',borderRadius:'0.75rem',outline:'none' }}/>
              <button className="btn-primary" onClick={saveTotal} style={{ padding:'0.5rem 1rem',fontSize:'0.8rem',background:'#9BAF9B' }}>Save</button>
              <button onClick={()=>{ setEditTotal(false); setTotalInput(String(totalBudget)) }} style={{ background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={16}/></button>
            </div>
          ) : (
            <button className="bt-total-btn" onClick={()=>{ setTotalInput(String(totalBudget||'')); setEditTotal(true) }}
              style={{ background:'white',border:'none',borderRadius:'0.75rem',padding:'0.6rem 1.25rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.5rem' }}>
              <span style={{ fontFamily:'Gibs,serif',fontSize:'1.5rem',color:totalBudget?'#1A1A1A':'#BCBCBC',fontWeight:400 }}>
                {totalBudget ? fmt(totalBudget) : 'Set budget ✎'}
              </span>
              {totalBudget>0&&(
                <div title="Edit total budget" style={{ width:28,height:28,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',transition:'background 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(179,107,85,0.1)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B36B55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </div>
              )}
            </button>
          )}
        </div>

        {/* Live health bar */}
        {totalBudget>0&&(
          <div style={{ marginTop:'1rem',paddingTop:'1rem',borderTop:'1px solid #F3F0EA' }}>
            <div className="bt-health-row" style={{ display:'flex',justifyContent:'space-between',marginBottom:'0.4rem' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'0.5rem' }}>
                <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#1A1A1A' }}>Overall Budget Health</span>
                {(()=>{ const hp=healthPill(healthPct); return <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.68rem',fontWeight:700,color:hp.color,background:hp.bg,borderRadius:4,padding:'2px 7px',letterSpacing:'0.05em',textTransform:'uppercase' }}>{hp.label}</span> })()}
              </div>
              <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',fontWeight:700,color:overBudget?'#C4826A':remaining<totalBudget*0.1?'#C4826A':'#9BAF9B' }}>
                {overBudget?`⚠ Over by ${fmt(Math.abs(remaining))}`:`${fmt(remaining)} remaining`}
              </span>
            </div>
            <div style={{ height:10,background:'#F3F0EA',borderRadius:100,overflow:'hidden' }}>
              <div style={{ height:'100%',width:`${healthPct}%`,borderRadius:100,transition:'width 0.6s ease',
                background:healthPct>90?'#D4847A':healthPct>65?'#D4A97A':`linear-gradient(to right, #A8C5A8, ${healthPct>45?'#D4C89A':'#A8C5A8'})` }}/>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',marginTop:'0.4rem' }}>
              <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.68rem',color:'#C4826A' }}>Committed: {fmt(summary.committed)}</span>
              <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.68rem',color:'#9BAF9B',fontWeight:700 }}>Paid: {fmt(summary.paid)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Free-plan cap banner */}
      {!isPremium && (
        <div style={{ background:'linear-gradient(135deg,rgba(184,134,11,0.06),rgba(196,130,106,0.05))', border:'1px solid rgba(184,134,11,0.25)', borderRadius:'0.875rem', padding:'0.875rem 1.125rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.875rem', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'0.6rem' }}>
            <Crown size={16} color="#B8860B" style={{ flexShrink:0, marginTop:2 }}/>
            <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.8rem', color:'#6B5A2A', margin:0, lineHeight:1.55 }}>
              You're on the <strong>free plan</strong> — budget and tracked expenses are capped at <strong>{fmt(freeCap)}</strong>. Upgrade for unlimited budgeting and CSV export.
            </p>
          </div>
          <button onClick={()=>navigate('/pricing')} style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.76rem', color:'white', background:'linear-gradient(135deg,#B8860B,#D4AF37)', border:'none', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>Upgrade</button>
        </div>
      )}

      {/* Budget tracker */}
      {(
        <>
          {/* Over-allocation alert */}
          {summary.allocated > totalBudget && totalBudget > 0 && (
            <div style={{ background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.3)', borderLeft:'3px solid #D97706', borderRadius:'0.875rem', padding:'1rem 1.25rem', display:'flex', flexDirection:'column', gap:'0.625rem' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:'0.625rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0,marginTop:1 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <div>
                  <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.85rem',color:'#92400E',margin:'0 0 0.2rem' }}>You are over-allocated by {fmt(summary.allocated-totalBudget)}</p>
                  <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',color:'#78350F',margin:0,lineHeight:1.6 }}>Your category budgets add up to more than your total wedding budget. Increase your total budget or reduce spending in one or more categories.</p>
                </div>
              </div>
              <div style={{ display:'flex',gap:'0.5rem',flexWrap:'wrap' }}>
                <button onClick={()=>{ setTotalInput(String(totalBudget||'')); setEditTotal(true) }} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#92400E',background:'rgba(217,119,6,0.1)',border:'1px solid rgba(217,119,6,0.3)',borderRadius:100,padding:'4px 12px',cursor:'pointer' }}>Increase Total Budget</button>
                <button onClick={()=>document.querySelector('[data-categories]')?.scrollIntoView({behavior:'smooth'})} style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#92400E',background:'none',border:'1px solid rgba(217,119,6,0.3)',borderRadius:100,padding:'4px 12px',cursor:'pointer' }}>Review Categories</button>
              </div>
            </div>
          )}
          {/* Near-budget info */}
          {summary.allocated <= totalBudget && totalBudget > 0 && (totalBudget - summary.allocated) < totalBudget * 0.05 && (totalBudget - summary.allocated) > 0 && (
            <div style={{ background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.2)', borderLeft:'3px solid #3B82F6', borderRadius:'0.875rem', padding:'0.875rem 1.25rem', display:'flex', gap:'0.625rem', alignItems:'flex-start' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0,marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',color:'#1E40AF',margin:0,lineHeight:1.6 }}>
                <strong>You are close to your budget limit</strong> — only {fmt(totalBudget-summary.allocated)} remaining to allocate across categories.
              </p>
            </div>
          )}

          {/* Budget Stats — 3 clear cards */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.625rem' }}>
            {[
              { label:'Total Budget', value:totalBudget,       color:'#1A1A1A' },
              { label:'Spent So Far', value:summary.committed, color:'#C4826A' },
              { label:'Still Owed',   value:summary.owed,      color: summary.owed>0 ? '#B36B55' : '#9BAF9B' },
            ].map(({ label, value, color })=>(
              <div key={label} className="card" style={{ padding:'0.875rem 1rem', textAlign:'center' }}>
                <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(0.95rem,2.5vw,1.25rem)', color, margin:'0 0 0.25rem', lineHeight:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {fmt(value)}
                </p>
                <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.6rem', color:'#9B9B9B', margin:0, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  {label}
                </p>
              </div>
            ))}
          </div>




          {/* Currency guide */}
          <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'-0.5rem 0 0.5rem', lineHeight:1.5 }}>
            Currency set to <strong>{currency.code}</strong>.{' '}
            <a href="/dashboard/settings#currency" style={{ color:'#C4826A', textDecoration:'none', fontWeight:600 }}>
              Change in Settings →
            </a>
          </p>

          {/* Contingency prompt */}
          {!hasContingency&&categories.length>0&&(
            <div style={{ background:'rgba(196,130,106,0.08)',border:'1.5px solid rgba(196,130,106,0.3)',borderRadius:'0.875rem',padding:'0.875rem 1.125rem',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'0.75rem' }}>
              <div>
                <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#1A1A1A',margin:'0 0 0.15rem' }}>⚡ Add a Contingency Fund</p>
                <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:0 }}>Experts recommend 10–15% of your budget for unexpected costs.</p>
              </div>
              <button className="btn-secondary" onClick={addContingency} style={{ fontSize:'0.78rem',padding:'0.5rem 1rem',flexShrink:0,borderColor:'#C4826A',color:'#C4826A' }}>
                Add 10% Buffer
              </button>
            </div>
          )}

          {/* Suggestions to add booked Aisle vendors to the budget — hidden when Aisle is off */}
          {flags.aisle !== false && <VendorBudgetSuggestions weddingId={wedding?.id} onAdded={fetchAll} />}

          {/* Categories — most recent first */}
          {loading ? (
            <p style={{ fontFamily:'Nunito Sans,sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem' }}>Loading…</p>
          ) : categories.length===0 ? (
            <div style={{ textAlign:'center',padding:'4rem 1rem' }}>
              <div style={{ fontSize:'3rem',marginBottom:'1rem' }}>💰</div>
              <h3 style={{ fontFamily:'Gibs,serif',fontWeight:400,fontSize:'1.2rem',color:'#1A1A1A',marginBottom:'0.5rem' }}>Let's map out your budget</h3>
              <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.875rem',color:'#6B6B6B',marginBottom:'1.5rem' }}>Start by adding a category from the presets.</p>
              <button className="btn-primary" onClick={()=>setShowPreset(true)} style={{ display:'inline-flex',alignItems:'center',gap:'0.5rem' }}><Plus size={15}/>Add from template</button>
            </div>
          ) : (
            <div data-categories style={{ display:'flex',flexDirection:'column',gap:'0.5rem' }}>
              {categories.map(cat=>(
                <CategoryCard key={cat.id} cat={cat} allItems={allItems} totalAllocated={summary.allocated}
                  onRefresh={fetchAll} currency={currency} onItemChange={handleItemChange}
                  capLimit={isPremium ? Infinity : freeCap} committedTotal={summary.committed} onUpgrade={()=>navigate('/pricing')}/>
              ))}
            </div>
          )}
        </>
      )}

      {/* Preset + Custom modal */}
      {showPreset&&(
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
          <div className="card fade-up" style={{ width:'100%',maxWidth:500,padding:'2rem',position:'relative',maxHeight:'85vh',overflowY:'auto' }}>
            <button onClick={()=>setShowPreset(false)} style={{ position:'absolute',top:14,right:14,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={18}/></button>
            <h3 style={{ fontFamily:'Gibs,serif',fontWeight:400,fontSize:'1.1rem',margin:'0 0 0.5rem' }}>Add Budget Category</h3>
            <p style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:'0 0 1.25rem' }}>Choose a preset or create your own.</p>

            {/* Presets */}
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',marginBottom:'1.25rem' }}>
              {PRESET_CATEGORIES.filter(p=>!categories.find(c=>c.name===p.name)).map(preset=>(
                <button key={preset.name} onClick={()=>addPreset(preset)} style={{ display:'flex',alignItems:'center',gap:'0.625rem',padding:'0.75rem',background:'white',border:'1.5px solid #E5E0D8',borderRadius:'0.75rem',cursor:'pointer',textAlign:'left',transition:'all 0.15s' }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor='#9BAF9B'; e.currentTarget.style.background='rgba(155,175,155,0.04)' }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.background='white' }}>
                  <span style={{ fontSize:'1.25rem' }}>{preset.icon}</span>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif',fontSize:'0.8rem',fontWeight:600,color:'#1A1A1A' }}>{preset.name}</span>
                </button>
              ))}
            </div>

            {/* Custom */}
            <div style={{ borderTop:'1px solid #E5E0D8',paddingTop:'1.25rem' }}>
              <p style={{ fontFamily:'Nunito Sans,sans-serif',fontWeight:700,fontSize:'0.8rem',color:'#1A1A1A',margin:'0 0 0.75rem' }}>✨ Custom Category</p>
              <div style={{ display:'flex',gap:'0.5rem',alignItems:'flex-end' }}>
                <div style={{ flex:'0 0 auto' }}>
                  <label className="label">Icon</label>
                  <input value={customIcon} onChange={e=>setCustomIcon(e.target.value)} maxLength={2}
                    style={{ width:56,fontFamily:'Nunito Sans,sans-serif',fontSize:'1.5rem',padding:'0.5rem',border:'1.5px solid #E5E0D8',borderRadius:'0.625rem',outline:'none',textAlign:'center' }}/>
                </div>
                <div style={{ flex:1 }}>
                  <label className="label">Category Name</label>
                  <input className="input-field" placeholder="e.g. Rehearsal Dinner" value={customName} onChange={e=>setCustomName(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&addCustom()}/>
                </div>
                <button className="btn-primary" onClick={addCustom} disabled={!customName.trim()} style={{ padding:'0.75rem 1rem',display:'flex',alignItems:'center',gap:'0.3rem',flexShrink:0 }}>
                  <Plus size={14}/> Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
